import { api } from './apiClient';

export interface UserSummary {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  last_login: string | null;
  total_sessions: number;
  total_minutes: number;
  active_sessions: number;
  total_actions: number;
  last_activity: string | null;
}

export interface SessionRecord {
  id: number;
  login_at: string;
  logout_at: string | null;
  duration_minutes: number | null;
  ip_address: string;
  user_agent: string;
  is_active: boolean;
}

export interface ActivityRecord {
  id: number;
  action: string;
  module: string;
  entity_type: string;
  entity_id: number | null;
  description: string;
  metadata: any;
  ip_address: string;
  created_at: string;
  full_name?: string;
  username?: string;
  role?: string;
}

export interface DashboardStats {
  totalUsers: number;
  activeNow: number;
  todaySessions: number;
  todayTotalMinutes: number;
  todayActions: number;
  recentActivities: ActivityRecord[];
  moduleBreakdown: { module: string; count: number }[];
  topUsers: {
    id: number;
    full_name: string;
    username: string;
    role: string;
    action_count: number;
    total_minutes: number;
  }[];
}

export interface UserDetail {
  user: {
    id: number;
    username: string;
    email: string;
    full_name: string;
    role: string;
    is_active: boolean;
    last_login: string;
    created_at: string;
  };
  totalMinutes: number;
  totalHours: string;
  sessions: SessionRecord[];
  moduleBreakdown: { module: string; count: number }[];
  actionBreakdown: { action: string; count: number }[];
  dailyActivity: { date: string; count: number }[];
}

export interface UserRecord {
  id: number;
  record_number: string;
  module: string;
  related_name: string;
  amount: number;
  status: string;
  created_at: string;
}

export interface RecordsSummary {
  module: string;
  label: string;
  count: number;
  total_amount: number;
  path: string;
}

export interface UserRecordsResponse {
  summary: RecordsSummary[];
  records: UserRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const adminService = {
  getActivityDashboard: async (): Promise<DashboardStats> => {
    return api.get<DashboardStats>('/admin/activity-dashboard');
  },

  getUsers: async (startDate?: string, endDate?: string): Promise<UserSummary[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString();
    const response = await api.get<{ users: UserSummary[] }>(`/admin/users${qs ? `?${qs}` : ''}`);
    return response.users;
  },

  getUserDetail: async (userId: number, startDate?: string, endDate?: string): Promise<UserDetail> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString();
    return api.get<UserDetail>(`/admin/users/${userId}${qs ? `?${qs}` : ''}`);
  },

  getUserSessions: async (userId: number, page = 1, limit = 20) => {
    return api.get(`/admin/users/${userId}/sessions?page=${page}&limit=${limit}`);
  },

  getUserActivities: async (
    userId: number,
    filters: { module?: string; action?: string; startDate?: string; endDate?: string } = {},
    page = 1,
    limit = 20
  ) => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (filters.module) params.append('module', filters.module);
    if (filters.action) params.append('action', filters.action);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    return api.get(`/admin/users/${userId}/activities?${params.toString()}`);
  },

  updateUserStatus: async (userId: number, is_active: boolean): Promise<void> => {
    await api.put(`/admin/users/${userId}/status`, { is_active });
  },

  getUserRecords: async (
    userId: number,
    filters: { module?: string; startDate?: string; endDate?: string } = {},
    page = 1,
    limit = 20
  ): Promise<UserRecordsResponse> => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (filters.module) params.append('module', filters.module);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    return api.get<UserRecordsResponse>(`/admin/users/${userId}/records?${params.toString()}`);
  },

  exportUserRecords: async (
    userId: number,
    filters: { module?: string; startDate?: string; endDate?: string } = {}
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (filters.module) params.append('module', filters.module);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    // Fetch as blob for CSV download
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/admin/users/${userId}/records/export?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      }
    );
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'records.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  sendHeartbeat: async (sessionId: number): Promise<void> => {
    try {
      await api.post('/auth/heartbeat', { sessionId });
    } catch {
      // Ignore heartbeat errors
    }
  },

  exportCompositeActivityPDF: async (userIds: number[]): Promise<void> => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/admin/users/composite-report/pdf`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({ userIds }),
      }
    );
    if (!response.ok) throw new Error('Failed to generate composite PDF report');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download =
      response.headers
        .get('Content-Disposition')
        ?.split('filename=')[1]
        ?.replace(/"/g, '') || 'composite_report.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  exportUserActivityPDF: async (userId: number): Promise<void> => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/admin/users/${userId}/report/pdf`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      }
    );
    if (!response.ok) throw new Error('Failed to generate PDF report');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download =
      response.headers
        .get('Content-Disposition')
        ?.split('filename=')[1]
        ?.replace(/"/g, '') || 'activity_report.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
